import {
  PREFIX_CURING,
  PREFIX_RH,
  PREFIX_T,
  PREFIX_WIND_MAG_KPH
} from '../shared/behaviour_models_utils';
import {calculateDIR} from '../shared/direction';

export {calculateDIR};

export function calculateGFDI(wd) {
  const gfdi = calculateFDI(wd);
  return typeof gfdi !== 'undefined' ? parseFloat(gfdi.toFixed(1)) : undefined;
}

export function calculateFDI(wd) {
  const relative_humidity = wd[PREFIX_RH];
  const air_temp = wd[PREFIX_T];
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const curing = wd[PREFIX_CURING];
  if (typeof relative_humidity !== 'undefined' &&
      typeof air_temp !== 'undefined' &&
      typeof curing !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    return Math.pow(10,
      (0.009254 - 0.004096 * Math.pow((100 - curing), 1.536) + 0.01201 * air_temp + 0.2789 * Math.pow(wind_speed, 0.5)
       - 0.09577 * Math.pow(relative_humidity, 0.5)));
  }
  return undefined;
}

export function calculateDFMC(wd) {
  const relative_humidity = wd[PREFIX_RH];
  const air_temp = wd[PREFIX_T];
  if (typeof relative_humidity !== 'undefined' &&
      typeof air_temp !== 'undefined') {
    return -0.205 * air_temp + 0.138 * relative_humidity + 9.58;
  }
  return undefined;
}

export function calculateFMC1(wd) {
  const dead_fuel_moisture = calculateDFMC(wd);
  if (typeof dead_fuel_moisture !== 'undefined') {
    return Math.exp(-0.108 * dead_fuel_moisture);
  }
  return undefined;
}

export function calculateFMC2(wd) {
  const dead_fuel_moisture = calculateDFMC(wd);
  if (typeof dead_fuel_moisture !== 'undefined') {
    return Math.max(0.001, 0.684 - 0.0342 * dead_fuel_moisture);
  }
  return undefined;
}

export function calculateFMC3(wd) {
  const dead_fuel_moisture = calculateDFMC(wd);
  if (typeof dead_fuel_moisture !== 'undefined') {
    return Math.max(0.001, 0.547 - 0.0228 * dead_fuel_moisture);
  }
  return undefined;
}

export function calculateFMC(wd) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const dead_fuel_moisture = calculateDFMC(wd);
  const fmc_1 = calculateFMC1(wd);
  const fmc_2 = calculateFMC2(wd);
  const fmc_3 = calculateFMC3(wd);
  if (typeof wind_speed !== 'undefined' &&
      typeof dead_fuel_moisture !== 'undefined') {
    if (dead_fuel_moisture < 12) {
      return fmc_1;
    }
    if (wind_speed > 10) {
      return fmc_3;
    }
    return fmc_2;
  }
  return undefined;
}

export function calculateCC(wd) {
  const curing = wd[PREFIX_CURING];
  if (typeof curing !== 'undefined') {
    return 1.12 / (1 + 59.2 * Math.exp(-0.124 * (curing - 50)));
  }
  return undefined;
}

export function calculateNAT_ROS_LW(wd, vars) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const curing_coefficient = calculateCC(wd, vars);
  const fuel_moisture_coefficient = calculateFMC(wd);
  if (typeof fuel_moisture_coefficient !== 'undefined' &&
      typeof curing_coefficient !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    return (0.054 + 0.269 * wind_speed) * fuel_moisture_coefficient * curing_coefficient;
  }
  return undefined;
}

export function calculateNAT_ROS_HW(wd, vars) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const curing_coefficient = calculateCC(wd, vars);
  const fuel_moisture_coefficient = calculateFMC(wd);
  if (typeof fuel_moisture_coefficient !== 'undefined' &&
      typeof curing_coefficient !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    return (1.4 + 0.838 * Math.pow((wind_speed - 5), 0.844)) * fuel_moisture_coefficient * curing_coefficient;
  }
  return undefined;
}

export function calculateGZD_ROS_LW(wd, vars) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const curing_coefficient = calculateCC(wd, vars);
  const fuel_moisture_coefficient = calculateFMC(wd);
  if (typeof fuel_moisture_coefficient !== 'undefined' &&
      typeof curing_coefficient !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    return (0.054 + 0.209 * wind_speed) * fuel_moisture_coefficient * curing_coefficient;
  }
  return undefined;
}

export function calculateGZD_ROS_HW(wd, vars) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const curing_coefficient = calculateCC(wd, vars);
  const fuel_moisture_coefficient = calculateFMC(wd);
  if (typeof fuel_moisture_coefficient !== 'undefined' &&
      typeof curing_coefficient !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    return (1.1 + 0.715 * Math.pow((wind_speed - 5), 0.844)) * fuel_moisture_coefficient * curing_coefficient;
  }
  return undefined;
}

export function calculateEO_ROS_HW(wd, vars) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const curing_coefficient = calculateCC(wd, vars);
  const fuel_moisture_coefficient = calculateFMC(wd);
  if (typeof fuel_moisture_coefficient !== 'undefined' &&
      typeof curing_coefficient !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    return (0.55 + 0.357 * Math.pow((wind_speed - 5), 0.844)) * fuel_moisture_coefficient * curing_coefficient;
  }
  return undefined;
}

export function calculateNAT_GROS(wd, vars) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const nat_ROS_LW = calculateNAT_ROS_LW(wd, vars);
  const nat_ROS_HW = calculateNAT_ROS_HW(wd, vars);
  if (typeof nat_ROS_LW !== 'undefined' &&
      typeof nat_ROS_HW !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    if (wind_speed < 5) {
      return nat_ROS_LW * 1000;
    }
    return nat_ROS_HW * 1000;
  }
  return undefined;
}

export function calculateNAT_GROS_SLP(wd, vars) {
  const nat_GROS = calculateNAT_GROS(wd, vars);
  if (typeof nat_GROS !== 'undefined') {
    return nat_GROS * Math.pow(1.0715, vars.slope);
  }
  return undefined;
}

export function calculateNAT_FROS(wd, vars) {
  const fmc = calculateFMC(wd);
  const cc = calculateCC(wd, vars);
  if (typeof fmc !== 'undefined' &&
      typeof cc !== 'undefined') {
    return (0.054 + 0.269 * 5) * fmc * cc * 1000;
  }
  return undefined;
}

export function calculateNAT_FH(wd, vars) {
  const nat_GROS_Slope = calculateNAT_GROS_SLP(wd, vars);
  if (typeof nat_GROS_Slope !== 'undefined') {
    return (0.003595 * 55.816 + 101.04 * Math.pow((nat_GROS_Slope / 1000), 0.3167)) /
           (55.816 + Math.pow((nat_GROS_Slope / 1000), 0.3167));
  }
  return undefined;
}

export function calculateNAT_FI(wd, vars) {
  const nat_GROS_Slope = calculateNAT_GROS_SLP(wd, vars);
  if (typeof nat_GROS_Slope !== 'undefined') {
    return 516.7 * nat_GROS_Slope * 6 / 1000;
  }
  return undefined;
}

export function calculateGZD_GROS(wd, vars) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const gzd_ROS_LW = calculateGZD_ROS_LW(wd, vars);
  const gzd_ROS_HW = calculateGZD_ROS_HW(wd, vars);
  if (typeof gzd_ROS_LW !== 'undefined' &&
      typeof gzd_ROS_HW !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    if (wind_speed < 5) {
      return gzd_ROS_LW * 1000;
    }
    return gzd_ROS_HW * 1000;
  }
  return undefined;
}

export function calculateGZD_GROS_SLP(wd, vars) {
  const gzd_GROS = calculateGZD_GROS(wd, vars);
  if (typeof gzd_GROS !== 'undefined') {
    return gzd_GROS * Math.pow(1.0715, vars.slope);
  }
  return undefined;
}

export function calculateGZD_FROS(wd, vars) {
  const fmc = calculateFMC(wd);
  const cc = calculateCC(wd, vars);
  if (typeof fmc !== 'undefined' &&
      typeof cc !== 'undefined') {
    return (0.054 + 0.209 * 5) * fmc * cc * 1000;
  }
  return undefined;
}

export function calculateGZD_FH(wd, vars) {
  const gzd_GROS_Slope = calculateGZD_GROS_SLP(wd, vars);
  if (typeof gzd_GROS_Slope !== 'undefined') {
    return (0.001316 * 149.4025 + 117.22 * Math.pow((gzd_GROS_Slope / 1000), 0.3141)) /
           (149.4025 + Math.pow((gzd_GROS_Slope / 1000), 0.3141));
  }
  return undefined;
}

export function calculateGZD_FI(wd, vars) {
  const gzd_GROS_Slope = calculateGZD_GROS_SLP(wd, vars);
  if (typeof gzd_GROS_Slope !== 'undefined') {
    return 516.7 * gzd_GROS_Slope * 4 / 1000;
  }
  return undefined;
}

export function calculateEO_GROS(wd, vars) {
  const wind_speed = wd[PREFIX_WIND_MAG_KPH];
  const eo_ROS_HW = calculateEO_ROS_HW(wd, vars);
  if (typeof eo_ROS_HW !== 'undefined' &&
      typeof wind_speed !== 'undefined') {
    if (wind_speed < 5) {
      return 0;
    }
    return eo_ROS_HW * 1000;
  }
  return undefined;
}

export function calculateEO_GROS_SLP(wd, vars) {
  const eo_GROS = calculateEO_GROS(wd, vars);
  if (typeof eo_GROS !== 'undefined') {
    return eo_GROS * Math.pow(1.0715, vars.slope);
  }
  return undefined;
}

export function calculateEO_FROS(wd, vars) {
  const fmc = calculateFMC(wd);
  const cc = calculateCC(wd, vars);
  if (typeof fmc !== 'undefined' &&
      typeof cc !== 'undefined') {
    return (0.55 + 0.357 * Math.pow((5 - 5), 0.844)) * fmc * cc * 1000;
  }
  return undefined;
}

export function calculateEO_FH(wd, vars) {
  const eo_GROS_Slope = calculateEO_GROS_SLP(wd, vars);
  if (typeof eo_GROS_Slope !== 'undefined') {
    return 1.0 / (32.915 - 29.388 * Math.pow((eo_GROS_Slope / 1000), 0.02969));
  }
  return undefined;
}

export function calculateEO_FI(wd, vars) {
  const eo_GROS_Slope = calculateEO_GROS_SLP(wd, vars);
  if (typeof eo_GROS_Slope !== 'undefined') {
    return 516.7 * eo_GROS_Slope * 1.5 / 1000;
  }
  return undefined;
}



// WEBPACK FOOTER //
// ./src/utils/behaviour_models/grass/calculations.js